import { MouseEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Badge,
  Button,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useChannel } from 'ably/react';

import { IncomingCallsApi, MasterNameApi } from '@/api';
import incomingCallAudio from '@/assets/audios/incoming-call.mp3';
import { IcPhone } from '@/assets/images';
import { NarrowButton } from '@/components';
import CallDetailsModal from '@/components/Modals/CallDetailsModal/CallDetailsModal';
import {
  ICall,
  IMasterName,
  IMasterNameOrganization,
  IMasterNamePerson,
  IUser,
  MasterNameType,
  Role,
} from '@/models';
import { isTargetRole } from '@/services';
import { useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPhoneNumber } from '@/utils';

import { CallCard } from './components/CallCard';
import IncomingCallCFSForm from './components/IncomingCallCFSForm';
import { IncomingCallEvent } from './data';
import {
  IncomingCallMenuRoot,
  IncomingCallModalRoot,
  IncomingCallRoot,
} from './styles';

const defaultModalValues = {
  open: false,
  title: '',
  phoneNumber: '',
  incomingCallId: '',
  externalCallId: '',
  callId: '',
};

export const IncomingCall = () => {
  const { account, user } = useUserStore();
  const navigate = useNavigate();
  const { updateToast } = useToastStore();
  const [modal, setModal] = useState(defaultModalValues);
  const [phoneNumberCounter, setPhoneNumberCounter] = useState<number>();
  const [incomingCalls, setIncomingCalls] = useState<ICall[]>([]);
  const [missedCalls, setMissedCalls] = useState<ICall[]>([]);
  const [activeCalls, setActiveCalls] = useState<ICall[]>([]);
  const [masterNames, setMasterNames] = useState<IMasterNamePerson[]>([]);
  const [selectedCall, setSelectedCall] = useState<ICall | null>(null);
  const [menuState, setMenuState] = useState<{ anchorEl: null | HTMLElement }>({
    anchorEl: null,
  });
  const [callDetailsModalOpen, setCallDetailsModalOpen] =
    useState<boolean>(false);
  const openActionMenu = Boolean(menuState.anchorEl);
  const menuElRef = useRef<HTMLButtonElement>(null);
  const incomingCallAudioRef = useRef<HTMLAudioElement | null>(null);

  const theme = useTheme();

  useChannel(`account:${account?._id}:incoming-call`, (message) => {
    if (message.name === IncomingCallEvent.MODIFIED) {
      const newCall = message.data;
      newCall.handledBy = message.data.handledBy;
      setActiveCalls((v) =>
        v.map((call) =>
          call._id === newCall._id ? { ...call, ...newCall } : call,
        ),
      );
      setIncomingCalls((v) =>
        v.map((call) =>
          call._id === newCall._id ? { ...call, ...newCall } : call,
        ),
      );
      setMissedCalls((v) =>
        v.map((call) =>
          call._id === newCall._id ? { ...call, ...newCall } : call,
        ),
      );
    }

    if (message.name === IncomingCallEvent.ANSWERED) {
      if (incomingCallAudioRef.current) {
        incomingCallAudioRef.current.pause();
        incomingCallAudioRef.current.currentTime = 0;
      }

      const newCall = message.data;
      newCall.isNewCall = true;
      newCall._id = message.extras.headers.incomingCall;
      setActiveCalls((v) => [newCall, ...v]);
      setIncomingCalls((v) => v.filter((call) => call._id !== newCall._id));
    } else if (message.name === IncomingCallEvent.READ) {
      if (incomingCallAudioRef.current) {
        incomingCallAudioRef.current.pause();
        incomingCallAudioRef.current.currentTime = 0;
      }
      const newCall = message.data;
      setActiveCalls((v) =>
        v.map((call) =>
          call._id === newCall._id ? { ...call, ...newCall } : call,
        ),
      );
      setIncomingCalls((v) =>
        v.map((call) =>
          call._id === newCall._id ? { ...call, ...newCall } : call,
        ),
      );
    } else if (message.name === IncomingCallEvent.MISSED) {
      const newCall = message.data;
      newCall.missed = true;
      setMissedCalls((v) => [newCall, ...v]);
      setIncomingCalls((v) => v.filter((call) => call._id !== newCall._id));
    } else if (message.name === IncomingCallEvent.CREATED) {
      incomingCallAudioRef.current = new Audio(incomingCallAudio);
      incomingCallAudioRef.current.play();

      const newCall = message.data;
      newCall.isNewCall = true;
      newCall._id = message.extras.headers.incomingCall;

      setIncomingCalls((v) => [newCall, ...v]);

      if (menuElRef.current) menuElRef.current.click();
    } else if (message.name === IncomingCallEvent.COMPLETED) {
      const newCall = message.data;

      setIncomingCalls((v) => v.filter((call) => call._id !== newCall?._id));
      setActiveCalls((v) => v.filter((call) => call._id !== newCall?._id));
    }
  });

  useEffect(() => {
    getCalls();
  }, []);

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) => {
    setMenuState({ anchorEl: e.currentTarget });
  };

  const handleCloseMenu = () => {
    setMenuState({ anchorEl: null });
  };

  const handleCloseModal = () => {
    setModal(defaultModalValues);
    setMasterNames([]);
  };

  const handleCallItemClick = (item: ICall) => {
    fetchData(item.phoneNumber, item._id);
    setMenuState({ anchorEl: null });
    setModal({
      open: true,
      phoneNumber: item.phoneNumber,
      incomingCallId: item._id,
      title: item.phoneNumber,
      externalCallId: item.externalCallId || '',
      callId: item.callId || '',
    });
  };

  const getCalls = async () => {
    try {
      const params = {
        page: 1,
        sort: JSON.stringify({ createdAt: -1 }),
        limit: 1000,
        lastHours: 5,
      };
      const res = await IncomingCallsApi.getCalls(params);

      const _missedCalls: ICall[] = [];
      const _activeCalls: ICall[] = [];
      const _incomingCalls: ICall[] = [];

      res.data.docs
        .filter((call) => !call.completedAt)
        .filter((call) => call.callId)
        .forEach((call: ICall) => {
          if (call.missed) {
            _missedCalls.push(call);
          } else if (call.answeredAt) {
            _activeCalls.push(call);
          } else {
            _incomingCalls.push(call);
          }
        });

      setIncomingCalls(_incomingCalls);
      setMissedCalls(_missedCalls);
      setActiveCalls(_activeCalls);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onOpenInfoModal = (call: ICall) => {
    setSelectedCall(call);
    setCallDetailsModalOpen(call ? true : true);
  };

  const fetchData = async (callPhoneNumber: string, callId: string) => {
    const masterNameParams = {
      search: callPhoneNumber,
      type: MasterNameType.PERSON,
      sort: JSON.stringify({ firstName: 1, lastName: 1 }),
    };
    Promise.all([
      MasterNameApi.getMasterNames(masterNameParams),
      IncomingCallsApi.updateCallReadStatus(callId),
    ])
      .then(([masterNamesRes, callStatusRes]) => {
        updateCallStatusCallback(callStatusRes.data.count, callId);
        fetchMasterNamesCallback(masterNamesRes.data, callPhoneNumber);
      })
      .catch((err) => {
        updateToast({ open: true, message: err.message });
      });
  };

  const fetchMasterNamesCallback = (
    fetchedMasterNames: IMasterName[],
    callerPhoneNumber: string,
  ) => {
    const isOrganizationCall =
      fetchedMasterNames[0]?.type === MasterNameType.ORGANIZATION;

    if (isOrganizationCall) {
      const { name } = fetchedMasterNames[0] as IMasterNameOrganization;

      const formattedTitle = `${formatPhoneNumber(callerPhoneNumber)}<br/>
        <span
          style="
            font-size: 17px;
            font-weight: 500;
          "
        >
          ${name}
        </span>
      `;

      setModal((v) => ({ ...v, title: formattedTitle }));
    }

    setMasterNames(
      !isOrganizationCall ? (fetchedMasterNames as IMasterNamePerson[]) : [],
    );
  };

  const updateCallStatusCallback = (count: number, callId: string) => {
    const restIncomingCalls = incomingCalls.filter(({ _id }) => _id !== callId);
    setPhoneNumberCounter(count);
    setIncomingCalls(restIncomingCalls);
  };

  return (
    <IncomingCallRoot style={{ zIndex: '99' }}>
      <Tooltip title="Call Logs">
        <NarrowButton
          style={{ backgroundColor: colors.error.main }}
          className="btn-circle"
          onClick={(e) => handleOpenMenu(e)}
          aria-controls={openActionMenu ? 'action-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openActionMenu ? 'true' : undefined}
          aria-label="Call Logs"
          ref={menuElRef}
        >
          <IcPhone />
        </NarrowButton>
      </Tooltip>

      <IncomingCallModalRoot
        open={modal.open}
        title={
          modal.externalCallId +
          ' ' +
          formatPhoneNumber(modal.title) +
          ' - ' +
          modal.callId
        }
        onClose={() => {}}
        hideCloseButton={true}
      >
        <>
          <Badge badgeContent={phoneNumberCounter} className="badge-counter" />
          <IncomingCallCFSForm
            phoneNumber={modal.phoneNumber}
            masterNames={masterNames}
            incomingCallId={modal.incomingCallId}
            onClose={handleCloseModal}
          />
        </>
      </IncomingCallModalRoot>

      <IncomingCallMenuRoot
        id="action-menu"
        anchorEl={menuState.anchorEl}
        open={openActionMenu}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Typography fontSize={16} fontWeight={500} textAlign="center" pb={2}>
          Active calls
        </Typography>
        {activeCalls.length === 0 && (
          <Typography
            fontSize={14}
            fontWeight={400}
            textAlign="center"
            color={theme.palette.text.secondary}
          >
            No active calls
          </Typography>
        )}
        {activeCalls.map((item) => (
          <MenuItem
            key={item._id}
            sx={{ padding: 0, mb: 1 }}
            onClick={() => {
              if (!item.isRead) handleCallItemClick(item);
            }}
          >
            <CallCard
              {...item}
              onInfoIconClick={() => onOpenInfoModal(item)}
              disabled={item.isRead}
            />
          </MenuItem>
        ))}

        <Typography fontSize={16} fontWeight={500} textAlign="center" p={2}>
          Incoming calls
        </Typography>
        {incomingCalls.filter((item) => !item.isRead).length === 0 && (
          <Typography
            fontSize={14}
            fontWeight={400}
            textAlign="center"
            color={theme.palette.text.secondary}
          >
            No incoming calls
          </Typography>
        )}
        {incomingCalls
          .filter((item) => !item.isRead)
          .map((item) => (
            <MenuItem
              key={item._id}
              sx={{ padding: 0, mb: 1 }}
              onClick={() => handleCallItemClick(item)}
            >
              <CallCard
                {...item}
                onInfoIconClick={() => onOpenInfoModal(item)}
              />
            </MenuItem>
          ))}

        <Typography fontSize={16} fontWeight={500} textAlign="center" p={2}>
          Missed calls
        </Typography>
        {missedCalls.slice(0, 3).length === 0 && (
          <Typography
            fontSize={14}
            fontWeight={400}
            textAlign="center"
            color={theme.palette.text.secondary}
          >
            No missed calls
          </Typography>
        )}
        {missedCalls.slice(0, 3).map((item) => (
          <MenuItem
            key={item._id}
            sx={{ padding: 0, mb: 1 }}
            onClick={() => {}}
          >
            <CallCard {...item} onInfoIconClick={() => onOpenInfoModal(item)} />
          </MenuItem>
        ))}
        {isTargetRole(user as IUser, Role.CALL_LOG_ACCESS) && (
          <Button
            variant="contained"
            color="error"
            sx={{ my: 2 }}
            fullWidth
            onClick={() => {
              navigate('/call-records');
              handleCloseMenu();
            }}
          >
            View Call Log Records
          </Button>
        )}
      </IncomingCallMenuRoot>
      {selectedCall && (
        <CallDetailsModal
          open={callDetailsModalOpen}
          call={selectedCall}
          onClose={() => {
            setCallDetailsModalOpen(false);
          }}
        />
      )}
    </IncomingCallRoot>
  );
};
